/**
 * Client-side IP geolocation with multi-provider fallback + 6h TTL cache.
 *
 * Going via the backend (`/api/geo`) proved unreliable because the
 * ingress chain rewrites `X-Forwarded-For`, so we resolve the user's
 * location directly from the browser. Order:
 *
 *   1. ipwho.is  — richest payload (country, city, currency, lat/lon)
 *   2. ipapi.co  — similar shape, different IP → provider route
 *   3. country.is — country-only last resort
 *
 * Each provider has a hard 3.5s abort. Resolved payloads are cached in
 * `localStorage` under `tc_ip_geo` for 6 hours so repeat loads don't
 * re-hit the public endpoints (and survive rate limits).
 */

const CACHE_KEY = "tc_ip_geo";
const TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const PROVIDER_TIMEOUT_MS = 3500;

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        if (!parsed.ts || !parsed.data) return null;
        if (Date.now() - parsed.ts > TTL_MS) return null;
        return parsed.data;
    } catch {
        return null;
    }
}

function writeCache(data) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
    } catch {
        /* quota or private mode — ignore */
    }
}

async function fetchWithTimeout(url, ms) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    try {
        const r = await fetch(url, { signal: ctrl.signal, cache: "no-store" });
        if (!r.ok) return null;
        return await r.json();
    } catch {
        return null;
    } finally {
        clearTimeout(t);
    }
}

function normaliseIpwho(d) {
    if (!d || d.success === false) return null;
    const cc = (d.country_code || "").toUpperCase();
    if (!cc) return null;
    return {
        country_code: cc,
        country_name: d.country || null,
        city: d.city || null,
        region: d.region || null,
        currency: (d.currency && (d.currency.code || d.currency)) || null,
        latitude: typeof d.latitude === "number" ? d.latitude : null,
        longitude: typeof d.longitude === "number" ? d.longitude : null,
        source: "ipwho.is",
    };
}

function normaliseIpapi(d) {
    if (!d || d.error) return null;
    const cc = (d.country_code || d.country || "").toUpperCase();
    if (!cc) return null;
    return {
        country_code: cc,
        country_name: d.country_name || null,
        city: d.city || null,
        region: d.region || null,
        currency: d.currency || null,
        latitude: typeof d.latitude === "number" ? d.latitude : null,
        longitude: typeof d.longitude === "number" ? d.longitude : null,
        source: "ipapi.co",
    };
}

function normaliseCountryIs(d) {
    const cc = (d && d.country || "").toUpperCase();
    if (!cc) return null;
    return {
        country_code: cc,
        country_name: null,
        city: null,
        region: null,
        currency: null,
        latitude: null,
        longitude: null,
        source: "country.is",
    };
}

/**
 * Returns a normalised geo payload from the public IP providers, or
 * `null` if all three fail. Uses the 6-hour cache when available.
 */
export async function fetchClientIpGeo({ force = false } = {}) {
    if (!force) {
        const cached = readCache();
        if (cached) return cached;
    }

    // 1. ipwho.is
    const a = await fetchWithTimeout("https://ipwho.is/", PROVIDER_TIMEOUT_MS);
    const aN = normaliseIpwho(a);
    if (aN) {
        writeCache(aN);
        return aN;
    }

    // 2. ipapi.co
    const b = await fetchWithTimeout("https://ipapi.co/json/", PROVIDER_TIMEOUT_MS);
    const bN = normaliseIpapi(b);
    if (bN) {
        writeCache(bN);
        return bN;
    }

    // 3. country.is (country-only last resort)
    const c = await fetchWithTimeout("https://api.country.is/", PROVIDER_TIMEOUT_MS);
    const cN = normaliseCountryIs(c);
    if (cN) {
        writeCache(cN);
        return cN;
    }

    return null;
}

export function clearClientIpGeoCache() {
    try {
        localStorage.removeItem(CACHE_KEY);
    } catch {
        /* ignore */
    }
}
